"use client";

import { cn } from "@/lib/utils";
import { Mail } from "lucide-react";
import { useEffect, useState } from "react";

/** 12-hour clock, no AM/PM (ambiguous on purpose — matches classic mock “9:41” vibe). */
function formatLockScreenTime(d: Date) {
  const h = d.getHours();
  const m = d.getMinutes();
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  const mm = String(m).padStart(2, "0");
  return `${hour12}:${mm}`;
}

function formatLockScreenDate(d: Date) {
  return new Intl.DateTimeFormat(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  }).format(d);
}

/**
 * Compact iPhone lock screen + Mail-style email banner (HTML/CSS). Copy mirrors estimate email subject / preview.
 * Clock / date follow the visitor's locale and update every second. Time is 12-hour digits only (no AM/PM).
 */
export function MarketingCustomerLinkMockup({ className }: { className?: string }) {
  const [now, setNow] = useState<Date | null>(null);

  useEffect(() => {
    setNow(new Date());
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const timeLabel = now ? formatLockScreenTime(now) : "9:41";
  const dateLine = now ? formatLockScreenDate(now) : "Friday, May 1";

  return (
    <div
      className={cn(
        "pointer-events-none select-none bg-gradient-to-b from-slate-100 to-slate-200/90 px-3 py-5 sm:px-4 sm:py-6",
        className
      )}
      aria-hidden
    >
      <div className="mx-auto flex max-w-[248px] justify-center origin-top scale-[0.78] sm:max-w-[268px] sm:scale-[0.84]">
        {/* Phone chassis */}
        <div
          className={cn(
            "relative w-[min(100%,220px)] shrink-0 rounded-[2.1rem] bg-zinc-900 p-[8px] sm:w-[min(100%,236px)] sm:p-[9px]",
            "shadow-[0_28px_56px_-12px_rgba(0,0,0,0.45),0_0_0_1px_rgba(255,255,255,0.06)_inset]"
          )}
        >
          {/* Screen */}
          <div className="relative aspect-[9/19.2] w-full overflow-hidden rounded-[1.65rem] bg-zinc-950 ring-1 ring-white/10">
            {/* Wallpaper */}
            <div
              className="absolute inset-0 bg-gradient-to-br from-sky-300/90 via-indigo-200/80 to-violet-400/90"
              aria-hidden
            />
            <div
              className="absolute inset-0 opacity-[0.18] mix-blend-overlay"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
              }}
            />

            {/* Top status + island */}
            <div className="relative z-10 flex flex-col items-center pt-2.5">
              <div className="h-6 w-[100px] rounded-full bg-black/85 ring-1 ring-white/[0.12] sm:h-7 sm:w-[108px]" />
              <div className="mt-1.5 flex w-full items-center justify-between px-4 text-[10px] font-semibold text-white/90 sm:mt-2 sm:px-5 sm:text-[11px]">
                <span suppressHydrationWarning>{timeLabel}</span>
                <div className="flex items-center gap-1 opacity-90">
                  <span className="text-[9px] sm:text-[10px]">5G</span>
                  <svg viewBox="0 0 24 12" className="h-2.5 w-6 text-white sm:h-3 sm:w-7" fill="currentColor" aria-hidden>
                    <rect x="0" y="3" width="18" height="7" rx="1.5" opacity="0.35" />
                    <rect x="0" y="3" width="14" height="7" rx="1.5" />
                    <path d="M20 4v4a2 2 0 0 0 2 2h1v-8h-1a2 2 0 0 0-2 2Z" opacity="0.9" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Clock */}
            <div className="relative z-0 mt-4 min-h-[5.5rem] px-4 text-center sm:mt-5 sm:min-h-[6rem] sm:px-5">
              <p className="text-[11px] font-medium text-white/75 sm:text-[13px]" suppressHydrationWarning>
                {dateLine}
              </p>
              <p
                className="mt-0.5 font-sans text-[2.65rem] font-extralight leading-none tracking-[-0.04em] text-white/95 tabular-nums sm:text-[3.1rem]"
                suppressHydrationWarning
              >
                {timeLabel}
              </p>
            </div>

            {/* Email (Mail app) notification */}
            <div className="relative z-20 mt-4 px-2.5 sm:mt-5 sm:px-3.5">
              <div
                className={cn(
                  "origin-top rounded-[1.15rem] border border-black/[0.06] bg-white/[0.94] p-2.5 shadow-[0_16px_36px_-10px_rgba(0,0,0,0.35)]",
                  "backdrop-blur-xl backdrop-saturate-150",
                  "animate-marketing-notif-pop"
                )}
              >
                <div className="flex gap-2">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-[#007AFF] shadow-sm ring-1 ring-black/5">
                    <Mail className="h-[42%] w-[42%] text-white" strokeWidth={2.25} aria-hidden />
                  </div>
                  <div className="min-w-0 flex-1 pt-0.5">
                    <div className="flex items-baseline justify-between gap-2">
                      <p className="truncate text-[10px] font-semibold text-slate-500">Mail</p>
                      <p className="shrink-0 text-[10px] text-slate-400">now</p>
                    </div>
                    <p className="truncate text-[10px] font-semibold leading-tight text-slate-800">Jorvio Quotes</p>
                    <p className="mt-0.5 font-display text-[12px] font-semibold leading-snug text-slate-900 sm:text-[13px]">
                      Your new estimate is ready
                    </p>
                    <p className="mt-0.5 line-clamp-2 text-[11px] leading-[1.35] text-slate-600">
                      You have a new estimate — tap to view in your browser.
                    </p>
                  </div>
                </div>
              </div>
              <p className="mt-2 px-1 text-center text-[9px] font-medium text-white/45 sm:text-[10px]">
                Swipe or tap to open
              </p>
            </div>

            <div className="absolute bottom-2 left-1/2 z-20 h-1 w-[84px] -translate-x-1/2 rounded-full bg-white/35 sm:w-[92px]" />
          </div>
        </div>
      </div>
    </div>
  );
}
